import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import api from "../services/api";
import EvenementTableAssignment from "./EvenementTableAssignment";
import "./EvenementList.css";

interface Evenement {
  _id: string;
  type: string;
  province: string;
  dateISO: string;
  heureISO: string;
  image: string;
  imageGeneree: boolean;
  rsvp: boolean;
  
  enfant?: string;
  annonce?: string;
  lieuCeremonie?: string;
  lieuReception?: string;
  parents?: string;
  
  personne?: string;
  typeFete?: string;
  lieu?: string;
  themeFete?: string;
  dresscode?: string;
  
  nomConference?: string;
  themeConference?: string;
  intervenants?: string;
  lieuConference?: string;
  organisateur?: string;
  programme?: string;
  
  nomEvenement?: string;
  introduction?: string;
  personnesConcernes?: string;
  lieuEvenement?: string;
  programmeAutre?: string;
  instructions?: string;
  
  createdAt: string;
}

interface Invite {
  _id: string;
  nom: string;
  telephone: string;
  table?: string;
  statut: string;
  lienEnvoye: boolean;
  uniqueToken: string;
  dateConfirmation?: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  evenements?: T[];
  evenement?: T;
  invites?: T[];
  tables?: string[];
  assignments?: Record<string, string>;
  protocoles?: any[];
  message?: string;
  error?: string;
  count?: number;
  skipped?: number;
}

interface Props {
  onSelectEvenement?: (id: string) => void;
  readOnly?: boolean;
  canDelete?: boolean;
  filterProvince?: string;
}

const EvenementList = ({ onSelectEvenement, readOnly = false, canDelete = true, filterProvince }: Props) => {
  const [evenements, setEvenements] = useState<Evenement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("tous");
  const [expandedEvenement, setExpandedEvenement] = useState<string | null>(null);
  const [invitesMap, setInvitesMap] = useState<Record<string, Invite[]>>({});
  const [loadingInvites, setLoadingInvites] = useState<Record<string, boolean>>({});
  const [tablesMap, setTablesMap] = useState<Record<string, string[]>>({});
  const [assignmentsMap, setAssignmentsMap] = useState<Record<string, Record<string, string>>>({});
  const [protocolesMap, setProtocolesMap] = useState<Record<string, any[]>>({});
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvenement, setSelectedEvenement] = useState<Evenement | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Evenement>>({});
  const [saving, setSaving] = useState(false);
  
  const [newInviteNom, setNewInviteNom] = useState("");
  const [newInviteTelephone, setNewInviteTelephone] = useState("");
  const [newInviteTable, setNewInviteTable] = useState("");
  const [addingInvite, setAddingInvite] = useState(false);
  const [importing, setImporting] = useState(false);
  
  const [activeTab, setActiveTab] = useState<"invites" | "tables">("invites");

  useEffect(() => {
    fetchEvenements();
  }, []);

  const getTitreEvenement = (evenement: Evenement): string => {
    switch(evenement.type) {
      case "bapteme":
        return `Baptême de ${evenement.enfant || ""}`;
      case "fete":
        return evenement.personne || "Fête";
      case "conference":
        return evenement.nomConference || "Conférence";
      case "autre":
        return evenement.nomEvenement || "Événement";
      default:
        return "Événement";
    }
  };

  const getDateEvenement = (evenement: Evenement): string => {
    const date = new Date(evenement.dateISO);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getHeureEvenement = (evenement: Evenement): string => {
    return evenement.heureISO || "";
  };

  const getLieuEvenement = (evenement: Evenement): string => {
    switch(evenement.type) {
      case "bapteme":
        return evenement.lieuCeremonie || evenement.lieuReception || "-";
      case "fete":
        return evenement.lieu || "-";
      case "conference":
        return evenement.lieuConference || "-";
      case "autre":
        return evenement.lieuEvenement || "-";
      default:
        return "-";
    }
  };

  const fetchEvenements = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse<Evenement>>("/evenements");
      if (response.data.success) {
        setEvenements(response.data.evenements || []);
      } else {
        setEvenements([]);
      }
    } catch (error) {
      console.error("Erreur chargement événements:", error);
      setEvenements([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async (evenementId: string) => {
    if (invitesMap[evenementId]) return;
    
    setLoadingInvites(prev => ({ ...prev, [evenementId]: true }));
    try {
      const [invitesRes, tablesRes, assignmentsRes, protocolesRes] = await Promise.all([
        api.get<ApiResponse<Invite>>(`/evenements/${evenementId}/invites`),
        api.get<ApiResponse>(`/evenements/${evenementId}/tables`),
        api.get<ApiResponse>(`/evenements/${evenementId}/assignments`),
        api.get<ApiResponse>(`/evenements/${evenementId}/protocoles-disponibles`)
      ]);
      
      if (invitesRes.data.success) {
        setInvitesMap(prev => ({ ...prev, [evenementId]: invitesRes.data.invites || [] }));
      }
      
      if (tablesRes.data.success) {
        setTablesMap(prev => ({ ...prev, [evenementId]: tablesRes.data.tables || [] }));
      }
      
      if (assignmentsRes.data.success) {
        setAssignmentsMap(prev => ({ ...prev, [evenementId]: assignmentsRes.data.assignments || {} }));
      }
      
      if (protocolesRes.data.success) {
        setProtocolesMap(prev => ({ ...prev, [evenementId]: protocolesRes.data.protocoles || [] }));
      }
      
    } catch (error) {
      console.error("Erreur chargement données:", error);
    } finally {
      setLoadingInvites(prev => ({ ...prev, [evenementId]: false }));
    }
  };

  const toggleExpand = (evenementId: string) => {
    if (expandedEvenement === evenementId) {
      setExpandedEvenement(null);
      setActiveTab("invites");
    } else {
      setExpandedEvenement(evenementId);
      setActiveTab("invites");
      fetchInvites(evenementId);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet événement ?")) {
      return;
    }
    try {
      const response = await api.delete<ApiResponse>(`/evenements/${id}`);
      if (response.data.success) {
        alert("Événement supprimé avec succès");
        fetchEvenements();
      } else {
        alert(response.data.message || "Erreur lors de la suppression");
      }
    } catch (error: any) {
      console.error("Erreur suppression:", error);
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}/evenement/${token}`;
    navigator.clipboard.writeText(link);
    alert("✅ Lien copié !");
  };

  const shareWhatsApp = (invite: Invite, evenement: Evenement) => {
    if (!invite.telephone) {
      alert(" Numéro de téléphone non renseigné");
      return;
    }
    
    const cleanPhone = invite.telephone.replace(/\s+/g, '').replace(/^\+/, '');
    const link = `${window.location.origin}/evenement/${invite.uniqueToken}`;
    const dateFormatted = getDateEvenement(evenement);
    const heureFormatted = getHeureEvenement(evenement);
    const lieuFormatted = getLieuEvenement(evenement);
    const titre = getTitreEvenement(evenement);
    
    const message = `🎊 *INVITATION ÉVÉNEMENT* 🎊

*Bonjour ${invite.nom},*

C'est avec une grande joie que nous vous invitons à célébrer cet événement spécial.

*📌 Événement :* ${titre}
*📅 Date :* ${dateFormatted}
*⏰ Heure :* ${heureFormatted}


*🔗 Lien d'invitation :* ${link}

*✅ Merci de confirmer votre présence en cliquant sur le lien ci-dessus.*

Nous serions ravis de vous compter parmi nous !

💝 Cordialement Bienvenue,
`;
    
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      bapteme: "Baptême",
      fete: "Fête",
      conference: "Conférence",
      autre: "Autre"
    };
    return labels[type] || type;
  };

  const addInvite = async (evenementId: string) => {
    if (!newInviteNom.trim()) {
      alert("Veuillez entrer un nom");
      return;
    }
    setAddingInvite(true);
    try {
      const response = await api.post<ApiResponse>(`/evenements/${evenementId}/invites`, {
        nom: newInviteNom,
        telephone: newInviteTelephone,
        table: newInviteTable
      });
      if (response.data.success) {
        alert("Invité ajouté avec succès");
        setNewInviteNom("");
        setNewInviteTelephone("");
        setNewInviteTable("");
        fetchInvites(evenementId);
      } else {
        alert(response.data.message || "Erreur lors de l'ajout");
      }
    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de l'ajout");
    } finally {
      setAddingInvite(false);
    }
  };

  const readExcelFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headers = jsonData[0] as string[];
            const rows = jsonData.slice(1);
            
            const formattedData = rows.map((row: any) => {
              const obj: any = {};
              headers.forEach((header, index) => {
                if (header && header.trim() !== "") {
                  obj[header.trim()] = row[index] || "";
                }
              });
              return obj;
            }).filter(row => Object.keys(row).length > 0);
            
            resolve(formattedData);
          } else {
            resolve([]);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const readCSVFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim() !== '');
          
          if (lines.length > 0) {
            let separator = '\t';
            if (lines[0].includes(',')) separator = ',';
            if (lines[0].includes(';')) separator = ';';
            
            const headers = lines[0].split(separator).map(h => h.trim());
            const rows = lines.slice(1);
            
            const formattedData = rows.map((line) => {
              const values = line.split(separator);
              const obj: any = {};
              headers.forEach((header, index) => {
                if (header && header.trim() !== "") {
                  obj[header.trim()] = values[index] ? values[index].trim() : "";
                }
              });
              return obj;
            }).filter(row => Object.keys(row).length > 0);
            
            resolve(formattedData);
          } else {
            resolve([]);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsText(file, 'UTF-8');
    });
  };

  const downloadExcelTemplate = (evenement: Evenement) => {
    const wb = XLSX.utils.book_new();
    
    const wsData = [
      ["Nom", "Téléphone", "Table"]
    ];
    
    for (let i = 0; i < 20; i++) {
      wsData.push(["", "", ""]);
    }
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const wscols = [
      { wch: 30 },
      { wch: 20 },
      { wch: 15 }
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, "Invités");
    
    const fileName = `invites-${getTitreEvenement(evenement)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportInvitesToExcel = (evenement: Evenement, invites: Invite[]) => {
    const wb = XLSX.utils.book_new();
    
    const wsData = [
      ["Nom", "Téléphone", "Table", "Statut", "Date de confirmation", "Lien d'invitation"]
    ];
    
    invites.forEach(invite => {
      wsData.push([
        invite.nom,
        invite.telephone || "-",
        invite.table || "-",
        invite.statut === "confirmed" ? "Confirmé" : 
        invite.statut === "declined" ? "Décliné" : "En attente",
        invite.dateConfirmation ? new Date(invite.dateConfirmation).toLocaleString('fr-FR') : "-",
        `${window.location.origin}/evenement/${invite.uniqueToken}`
      ]);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const wscols = [
      { wch: 30 },
      { wch: 20 },
      { wch: 15 },
      { wch: 12 },
      { wch: 20 },
      { wch: 50 }
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, "Invités");
    
    const fileName = `invites-${getTitreEvenement(evenement)}-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>, evenementId: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
      alert("Veuillez sélectionner un fichier Excel (.csv, .xlsx, .xls)");
      return;
    }

    handleImportInvites(evenementId, file);
  };

  const handleImportInvites = async (evenementId: string, file: File) => {
    setImporting(true);

    try {
      let guestsData: any[] = [];
      
      if (file.name.endsWith('.csv')) {
        guestsData = await readCSVFile(file);
      } else if (file.name.match(/\.(xlsx|xls)$/i)) {
        guestsData = await readExcelFile(file);
      } else {
        alert("Format de fichier non supporté");
        return;
      }

      if (guestsData.length === 0) {
        alert("Aucune donnée trouvée dans le fichier");
        return;
      }

      const normalizedData = guestsData.map((guest) => {
        const nom = guest.Nom || guest.NOM || guest.nom || guest['Nom complet'] || guest.Name || '';
        const telephone = guest.Téléphone || guest.TELEPHONE || guest.telephone || 
                         guest.Tél || guest.tel || guest['Numéro téléphone'] || guest.Phone || '';
        const table = guest.Table || guest.TABLE || guest.table || '';

        let normalizedPhone = telephone.toString().trim();
        normalizedPhone = normalizedPhone.replace(/\s+/g, '');
        normalizedPhone = normalizedPhone.replace(/[^\d+]/g, '');
        
        if (normalizedPhone && !normalizedPhone.startsWith('+')) {
          normalizedPhone = '+243' + normalizedPhone;
        }

        return {
          nom: nom.toString().trim(),
          telephone: normalizedPhone,
          table: table.toString().trim()
        };
      }).filter(guest => guest.nom !== "");

      if (normalizedData.length === 0) {
        alert("Aucune donnée valide après traitement");
        return;
      }

      const response = await api.post<ApiResponse>(`/evenements/${evenementId}/invites/batch`, {
        invites: normalizedData.map(guest => ({
          nom: guest.nom,
          telephone: guest.telephone,
          table: guest.table,
          statut: "pending"
        }))
      });

      if (response.data.success) {
        alert(`${normalizedData.length} invités créés avec succès !`);
        fetchInvites(evenementId);
      } else {
        alert(`Erreur: ${response.data.message || "Impossible de créer les invitations"}`);
      }

    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Une erreur est survenue lors de la lecture du fichier");
    } finally {
      setImporting(false);
    }
  };

  const deleteInvite = async (evenementId: string, inviteId: string) => {
    if (!window.confirm("Supprimer cet invité ?")) return;
    try {
      const response = await api.delete<ApiResponse>(`/evenements/${evenementId}/invites/${inviteId}`);
      if (response.data.success) {
        fetchInvites(evenementId);
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const openEditModal = (evenement: Evenement) => {
    setSelectedEvenement(evenement);
    setEditFormData(evenement);
    setShowEditModal(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: checked }));
  };

  const saveEdit = async () => {
    if (!selectedEvenement) return;
    setSaving(true);
    try {
      const response = await api.put<ApiResponse>(`/evenements/${selectedEvenement._id}`, editFormData);
      if (response.data.success) {
        alert("Événement modifié avec succès");
        setShowEditModal(false);
        fetchEvenements();
      } else {
        alert(response.data.message || "Erreur lors de la modification");
      }
    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const filteredEvenements = filter === "tous" 
    ? evenements 
    : evenements.filter(e => e.type === filter);

  if (loading) {
    return <div className="loading">Chargement des événements...</div>;
  }

  return (
    <div className="evenement-list-container">
      <div className="list-header">
        <h2>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
          </svg>
          Liste des événements
        </h2>
        
        <div className="filter-section">
          <label>Filtrer par type :</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="tous">Tous</option>
            <option value="bapteme">Baptêmes</option>
            <option value="fete">Fêtes</option>
            <option value="conference">Conférences</option>
            <option value="autre">Autres</option>
          </select>
        </div>
      </div>

      <div className="evenement-stats">
        <div className="stat-card">
          <span className="stat-value">{evenements.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{evenements.filter(e => e.type === "bapteme").length}</span>
          <span className="stat-label">Baptêmes</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{evenements.filter(e => e.type === "fete").length}</span>
          <span className="stat-label">Fêtes</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{evenements.filter(e => e.type === "conference").length}</span>
          <span className="stat-label">Conférences</span>
        </div>
      </div>

      {filteredEvenements.length === 0 ? (
        <p className="empty-message">Aucun événement trouvé</p>
      ) : (
        <div className="table-container">
          <table className="evenement-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Titre</th>
                <th>Date</th>
                <th>Heure</th>
                <th>Lieu</th>
                <th>RSVP</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvenements.map(evenement => (
                <React.Fragment key={evenement._id}>
                  <tr className={expandedEvenement === evenement._id ? "expanded-row" : ""}>
                    <td className="type-cell">
                      <span className={`type-badge ${evenement.type}`}>
                        {getTypeLabel(evenement.type)}
                      </span>
                    </td>
                    <td className="titre-cell">{getTitreEvenement(evenement)}</td>
                    <td>{new Date(evenement.dateISO).toLocaleDateString('fr-FR')}</td>
                    <td>{evenement.heureISO}</td>
                    <td>{getLieuEvenement(evenement)}</td>
                    <td>
                      {evenement.rsvp ? (
                        <span className="rsvp-badge active">Activé</span>
                      ) : (
                        <span className="rsvp-badge inactive">Désactivé</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button className="btn-action" title="Voir les invités" onClick={() => toggleExpand(evenement._id)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                        </svg>
                      </button>
                      <button className="btn-action edit" title="Modifier" onClick={() => openEditModal(evenement)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                        </svg>
                      </button>
                      <button className="btn-action delete" title="Supprimer" onClick={() => handleDelete(evenement._id)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                        </svg>
                      </button>
                      {onSelectEvenement && (
                        <button
                          className="assign-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectEvenement(evenement._id);
                          }}
                          title="Assigner des tables à des protocoles"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                          </svg>
                          Assigner tables
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedEvenement === evenement._id && (
                    <tr className="invites-row">
                      <td colSpan={7}>
                        <div className="invites-container">
                          <div className="invites-header">
                            <h4>Gestion de l'événement</h4>
                            <div className="tab-buttons">
                              <button 
                                className={`tab-btn ${activeTab === "invites" ? "active" : ""}`}
                                onClick={() => setActiveTab("invites")}
                              >
                                Invités ({invitesMap[evenement._id]?.length || 0})
                              </button>
                              <button 
                                className={`tab-btn ${activeTab === "tables" ? "active" : ""}`}
                                onClick={() => setActiveTab("tables")}
                              >
                                Tables ({tablesMap[evenement._id]?.length || 0})
                              </button>
                            </div>
                          </div>
                          
                          {activeTab === "invites" && (
                            <>
                              <div className="invites-actions-bar">
                                <button 
                                  className="template-btn"
                                  onClick={() => downloadExcelTemplate(evenement)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                                  </svg>
                                  Télécharger modèle
                                </button>
                                <button 
                                  className="export-btn"
                                  onClick={() => exportInvitesToExcel(evenement, invitesMap[evenement._id] || [])}
                                  disabled={!invitesMap[evenement._id]?.length}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                                  </svg>
                                  Exporter Excel
                                </button>
                                <input 
                                  type="file" 
                                  id={`excel-upload-${evenement._id}`}
                                  accept=".csv,.xlsx,.xls"
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleExcelUpload(e, evenement._id);
                                    }
                                  }}
                                />
                                <button 
                                  className="upload-btn"
                                  onClick={() => document.getElementById(`excel-upload-${evenement._id}`)?.click()}
                                  disabled={importing}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" fill="currentColor"/>
                                  </svg>
                                  Importer Excel
                                </button>
                              </div>
                              
                              <div className="add-invite-form">
                                <input 
                                  type="text" 
                                  placeholder="Nom complet *" 
                                  value={newInviteNom} 
                                  onChange={(e) => setNewInviteNom(e.target.value)} 
                                />
                                <input 
                                  type="tel" 
                                  placeholder="Téléphone (WhatsApp)" 
                                  value={newInviteTelephone} 
                                  onChange={(e) => setNewInviteTelephone(e.target.value)} 
                                />
                                <input 
                                  type="text" 
                                  placeholder="Table (optionnel)" 
                                  value={newInviteTable} 
                                  onChange={(e) => setNewInviteTable(e.target.value)} 
                                  list={`tables-list-${evenement._id}`}
                                />
                                <datalist id={`tables-list-${evenement._id}`}>
                                  {tablesMap[evenement._id]?.map(table => (
                                    <option key={table} value={table} />
                                  ))}
                                </datalist>
                                <button 
                                  className="add-invite-btn" 
                                  onClick={() => addInvite(evenement._id)} 
                                  disabled={addingInvite}
                                >
                                  {addingInvite ? "Ajout..." : "+ Ajouter"}
                                </button>
                              </div>
                              
                              {loadingInvites[evenement._id] ? (
                                <div className="loading-spinner"><div className="spinner"></div></div>
                              ) : !invitesMap[evenement._id]?.length ? (
                                <p className="empty-invites">Aucun invité pour cet événement</p>
                              ) : (
                                <table className="invites-table">
                                  <thead>
                                    <tr>
                                      <th>Nom</th>
                                      <th>Téléphone</th>
                                      <th>Table</th>
                                      <th>Statut</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invitesMap[evenement._id].map(invite => (
                                      <tr key={invite._id}>
                                        <td>{invite.nom}</td>
                                        <td>{invite.telephone || "-"}</td>
                                        <td>{invite.table || "-"}</td>
                                        <td>
                                          <span className={`status-badge ${invite.statut}`}>
                                            {invite.statut === "confirmed" ? "Confirmé" : 
                                             invite.statut === "declined" ? "Décliné" : "En attente"}
                                          </span>
                                        </td>
                                        <td className="actions-cell">
                                          <button 
                                            className="action-btn copy" 
                                            onClick={() => copyInviteLink(invite.uniqueToken)} 
                                            title="Copier le lien"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
                                            </svg>
                                          </button>
                                          {invite.telephone && (
                                            <button 
                                              className="action-btn whatsapp" 
                                              onClick={() => shareWhatsApp(invite, evenement)} 
                                              title="Envoyer sur WhatsApp"
                                            >
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.05 4.91C17.18 3.24 14.81 2.2 12.2 2.04 5.95 1.56 2.2 6.38 2.2 9.4c0 1.4.5 2.7 1.3 3.8L3 18.9l5.8-1.6c1.2.7 2.5 1.1 3.9 1.1 5.3 0 9.1-4.8 9.1-9.1 0-2.4-1-4.7-2.8-6.4z" fill="currentColor"/>
                                              </svg>
                                            </button>
                                          )}
                                          <button 
                                            className="action-btn delete" 
                                            onClick={() => deleteInvite(evenement._id, invite._id)} 
                                            title="Supprimer"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                                            </svg>
                                          </button>
                                         </td>
                                       </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </>
                          )}
                          
                          {activeTab === "tables" && (
                            <EvenementTableAssignment 
                              evenementId={evenement._id}
                              tables={tablesMap[evenement._id] || []}
                              assignments={assignmentsMap[evenement._id] || {}}
                              protocoles={protocolesMap[evenement._id] || []}
                              onUpdate={() => fetchInvites(evenement._id)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODALE MODIFICATION */}
      {showEditModal && selectedEvenement && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-edit" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier l'événement</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
                <div className="form-group">
                  <label>Type</label>
                  <select name="type" value={editFormData.type || ""} onChange={handleEditChange}>
                    <option value="bapteme">Baptême</option>
                    <option value="fete">Fête</option>
                    <option value="conference">Conférence</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" name="dateISO" value={editFormData.dateISO || ""} onChange={handleEditChange} />
                  </div>
                  <div className="form-group">
                    <label>Heure</label>
                    <input type="time" name="heureISO" value={editFormData.heureISO || ""} onChange={handleEditChange} />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Province</label>
                  <input type="text" name="province" value={editFormData.province || ""} onChange={handleEditChange} />
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input type="checkbox" name="rsvp" checked={editFormData.rsvp || false} onChange={handleCheckboxChange} />
                    Activer RSVP
                  </label>
                </div>
                
                <div className="modal-actions">
                  <button type="submit" className="save-btn" disabled={saving}>
                    {saving ? "Enregistrement..." : "Enregistrer"}
                  </button>
                  <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvenementList;